/**
 * Workbook + per-sheet info scanner.
 *
 * Walks every worksheet, gets its used range, and counts cells by kind.
 * Bounded by Excel's used-range tracking — we don't iterate the full sheet
 * grid. For empty sheets, returns zero-counts without ever loading values.
 */
import type { NamedRangeSummary, SheetInfo, WorkbookInfo } from "./types";
import { scanNamedRanges } from "../namedRanges/scanner";
import { getWorkbookModel, Origin } from "../model";
import { addressFromRC } from "../rules/audit/addressing";


/**
 * Workbook info scanner. Shares the workbook scan with audit /
 * strings / formulas — if they ran first, this is essentially free.
 *
 * Counts (used / blank / formula) come from cellOrigin: EMPTY → blank,
 * anything else → used, with FORMULA tallied as a subset of used.
 *
 * Empty sheets (no used range) are surfaced via the workbook-wide
 * sheetsBySheetId catalog rather than the (filtered) model.sheets map,
 * so hidden + empty sheets still show up in the Info tab with
 * zero-counts.
 *
 * Named ranges still come from scanNamedRanges (its own Excel.run) —
 * the model's definedNamesCatalog has bounds but not the richer fields
 * (formula text, isValid) the Info tab displays.
 */
export async function scanWorkbookInfo(): Promise<WorkbookInfo> {
  const model = await getWorkbookModel("workbook");

  const sheetInfos: SheetInfo[] = [];
  for (const [sheetId, meta] of Array.from(model.catalogs.sheetsBySheetId)) {
    const sheet = model.sheets.get(sheetId);

    if (!sheet) {
      // Empty sheet — no SheetModel built. Surface the metadata anyway.
      sheetInfos.push({
        name: meta.name,
        position: meta.position,
        visibility: meta.visibility,
        usedRange: null,
        rowCount: 0,
        columnCount: 0,
        usedCellCount: 0,
        blankCellCount: 0,
        formulaCellCount: 0,
      });
      continue;
    }

    const { rows, cols, startRow, startCol } = sheet.bounds;
    const cellOrigin = sheet.cellOrigin;
    let used = 0;
    let blank = 0;
    let formulas = 0;
    const n = rows * cols;
    for (let idx = 0; idx < n; idx++) {
      const origin = cellOrigin[idx];
      if (origin === Origin.EMPTY) {
        blank++;
      } else {
        used++;
        if (origin === Origin.FORMULA) formulas++;
      }
    }

    const lastRow = startRow + rows - 1;
    const lastCol = startCol + cols - 1;
    const address = `${addressFromRC(startRow, startCol)}:${addressFromRC(lastRow, lastCol)}`;

    sheetInfos.push({
      name: meta.name,
      position: meta.position,
      visibility: meta.visibility,
      usedRange: address,
      rowCount: rows,
      columnCount: cols,
      usedCellCount: used,
      blankCellCount: blank,
      formulaCellCount: formulas,
    });
  }

  sheetInfos.sort((a, b) => a.position - b.position);

  const visibleCount = sheetInfos.filter((s) => s.visibility === "visible").length;
  const hiddenCount = sheetInfos.length - visibleCount;
  const totalUsed = sheetInfos.reduce((acc, x) => acc + x.usedCellCount, 0);
  const totalFormulas = sheetInfos.reduce((acc, x) => acc + x.formulaCellCount, 0);

  let names: NamedRangeSummary[] = [];
  try {
    const full = await scanNamedRanges();
    names = full.map((n) => ({
      name: n.name,
      scope: n.scope,
      formula: n.formula,
      isValid: n.isValid,
    }));
  } catch {
    // Swallow — the rest of the Info tab is still usable.
  }

  return {
    filename: model.workbookName,
    sheetCount: sheetInfos.length,
    visibleSheetCount: visibleCount,
    hiddenSheetCount: hiddenCount,
    namedRangeCount: names.length,
    totalUsedCells: totalUsed,
    totalFormulaCells: totalFormulas,
    sheets: sheetInfos,
    namedRanges: names,
  };
}

